// Restauration du défilement par écran (Ben 23/09).
//
// Vécu : Virginie descend loin dans une liste, ouvre la fiche Galke, revient
// — et l'écran est remonté en haut : elle ne sait plus où elle en était. Le
// routeur de l'app (createBrowserRouter) ne restaure rien, et les listes
// longues défilent souvent dans un CONTENEUR interne (tableau virtualisé à
// hauteur bornée), que ni le navigateur ni `<ScrollRestoration>` ne suivent.
//
// Principe : on NOTE en continu la position de la fenêtre et de chaque
// conteneur défilant de l'écran (clé = chemin + paramètres, en
// sessionStorage : survit à F5 et au retour depuis un site externe dans le
// même onglet), puis, au retour sur l'écran (Précédent/Suivant ou
// rechargement), on la RE-APPLIQUE dès que le contenu est assez haut — les
// données arrivent après le montage, d'où les tentatives répétées, bornées
// dans le temps et abandonnées au premier geste de l'utilisatrice.
//
// Ce fichier ne contient que la mécanique DOM, sans React : testable seul.

const PREFIXE_STOCKAGE = 'herbo:defilement:';

/** Au-delà, on renonce : l'écran n'atteindra plus la hauteur attendue. */
export const DELAI_MAX_RESTAURATION_MS = 10_000;

/**
 * Position d'un conteneur. `ancre` = première ligne visible d'un tableau
 * virtualisé (`data-index`, posé par `@tanstack/react-virtual`) et son écart
 * au haut du conteneur : les pixels seuls ne suffisent pas, le virtualiseur
 * mesure ses lignes APRÈS le saut et recale le défilement — la ligne visible
 * glissait d'un cran (mesuré sur la proposition MP : −73 px, 1 à 2 s après).
 */
export interface PositionConteneur {
  readonly top: number;
  readonly ancre?: { readonly index: string; readonly decalage: number };
}

export interface InstantaneDefilement {
  /** `window.scrollY` */
  readonly fenetre: number;
  /** signature du conteneur → position */
  readonly conteneurs: Readonly<Record<string, PositionConteneur>>;
}

export function cleEcran(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

function stockage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function lireInstantane(cle: string): InstantaneDefilement | null {
  try {
    const brut = stockage()?.getItem(PREFIXE_STOCKAGE + cle);
    if (!brut) return null;
    const v = JSON.parse(brut) as Partial<InstantaneDefilement>;
    if (typeof v.fenetre !== 'number' || typeof v.conteneurs !== 'object' || v.conteneurs == null) return null;
    // Lecture tolérante : une entrée illisible est ignorée, pas l'écran entier.
    const conteneurs: Record<string, PositionConteneur> = {};
    for (const [sig, pos] of Object.entries(v.conteneurs)) {
      if (pos && typeof pos === 'object' && typeof pos.top === 'number') conteneurs[sig] = pos;
    }
    return { fenetre: v.fenetre, conteneurs };
  } catch {
    return null;
  }
}

export function ecrireInstantane(cle: string, inst: InstantaneDefilement): void {
  try {
    stockage()?.setItem(PREFIXE_STOCKAGE + cle, JSON.stringify(inst));
  } catch {
    // Stockage plein ou bloqué : la restauration est un confort, jamais bloquante.
  }
}

/** Racine explorée : le contenu de l'écran (`<main>`), pas la barre du haut. */
function racine(): ParentNode {
  return document.querySelector('main') ?? document.body;
}

function estDefilant(el: Element): boolean {
  const oy = getComputedStyle(el).overflowY;
  return oy === 'auto' || oy === 'scroll';
}

/**
 * Signature STABLE d'un conteneur d'un rendu à l'autre : balise + classes +
 * rang parmi les éléments identiques de l'écran (plusieurs tableaux de même
 * style sur une page). Aucun attribut à poser dans les modules : tout
 * conteneur défilant existant est suivi tel quel.
 */
export function signatureConteneur(el: Element): string | null {
  const tag = el.tagName.toLowerCase();
  const classes = typeof el.className === 'string' ? el.className : '';
  let rang = 0;
  for (const autre of Array.from(racine().querySelectorAll(tag))) {
    if (autre === el) return `${tag}|${classes}|${rang}`;
    if ((typeof autre.className === 'string' ? autre.className : '') === classes) rang++;
  }
  return null; // hors de l'écran (barre du haut, élément détaché)
}

/** Retrouve un conteneur à partir de sa signature (ou `null` s'il n'est pas encore rendu). */
export function trouverConteneur(signature: string): HTMLElement | null {
  const [tag, classes, rangTxt] = signature.split('|');
  if (!tag || rangTxt == null) return null;
  const rangCible = Number(rangTxt);
  let rang = 0;
  for (const el of Array.from(racine().querySelectorAll<HTMLElement>(tag))) {
    if ((typeof el.className === 'string' ? el.className : '') !== classes) continue;
    if (rang === rangCible) return el;
    rang++;
  }
  return null;
}

/**
 * Tente d'appliquer l'instantané. Une cible n'est « atteinte » que lorsque le
 * contenu est assez haut pour la porter (sinon le navigateur la tronquerait,
 * et on reposerait l'utilisatrice au mauvais endroit en croyant avoir fini).
 * Retourne les cibles restantes.
 */
export function appliquerInstantane(inst: InstantaneDefilement): InstantaneDefilement {
  const restants: Record<string, PositionConteneur> = {};
  for (const [sig, pos] of Object.entries(inst.conteneurs)) {
    const el = trouverConteneur(sig);
    if (el && el.scrollHeight - el.clientHeight >= pos.top - 1) {
      const ligne = pos.ancre ? ligneAncre(el, pos.ancre.index) : null;
      if (ligne && pos.ancre) {
        // Ligne-repère rendue : c'est ELLE qui doit revenir à sa place.
        const ecart = ecartAuHaut(el, ligne) - pos.ancre.decalage;
        if (Math.abs(ecart) <= 1) continue;
        el.scrollTop += ecart;
      } else {
        if (Math.abs(el.scrollTop - pos.top) > 1) el.scrollTop = pos.top;
        if (!pos.ancre && Math.abs(el.scrollTop - pos.top) <= 1) continue;
      }
    } else if (el && pos.ancre) {
      // Tableau virtualisé encore trop court : sa hauteur n'est qu'une
      // ESTIMATION tant que les lignes du bas n'ont pas été rendues et
      // mesurées (« Commande à nous-mêmes » 1280 : 2 473 px atteignables pour
      // 2 539 notés). Attendre ne sert à rien — elle ne grandira que si on y
      // va : on s'en approche au maximum, les lignes se mesurent, et la
      // tentative suivante pose la ligne-repère. Jamais « atteint » ici.
      const max = el.scrollHeight - el.clientHeight;
      if (max > 0 && el.scrollTop < max - 1) el.scrollTop = max;
    }
    restants[sig] = pos;
  }
  let fenetre = inst.fenetre;
  if (fenetre > 0) {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max >= fenetre - 1) {
      if (Math.abs(window.scrollY - fenetre) > 1) window.scrollTo(0, fenetre);
      if (Math.abs(window.scrollY - fenetre) <= 1) fenetre = 0;
    }
  }
  return { fenetre, conteneurs: restants };
}

export function estTermine(inst: InstantaneDefilement): boolean {
  return inst.fenetre <= 0 && Object.keys(inst.conteneurs).length === 0;
}

function ecartAuHaut(conteneur: Element, ligne: Element): number {
  return Math.round(ligne.getBoundingClientRect().top - conteneur.getBoundingClientRect().top);
}

function ligneAncre(conteneur: Element, index: string): Element | null {
  for (const ligne of Array.from(conteneur.querySelectorAll('[data-index]'))) {
    if (ligne.getAttribute('data-index') === index) return ligne;
  }
  return null;
}

/** Position courante d'un conteneur défilant, pour mise à jour de l'instantané. */
export function releverConteneur(el: Element): { signature: string; position: PositionConteneur } | null {
  if (!estDefilant(el)) return null;
  const signature = signatureConteneur(el);
  if (!signature) return null;
  const top = Math.round(el.scrollTop);
  const hautConteneur = el.getBoundingClientRect().top;
  const premiere = Array.from(el.querySelectorAll('[data-index]')).find(
    (l) => l.getBoundingClientRect().bottom > hautConteneur,
  );
  const index = premiere?.getAttribute('data-index');
  return {
    signature,
    position: premiere && index != null ? { top, ancre: { index, decalage: ecartAuHaut(el, premiere) } } : { top },
  };
}
